import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ScraiiAiComponent } from '../direct/page/scraii-ai/scraii-ai.component';
import { AuthService } from 'src/app/services/auth.service';
import { FilterService } from 'src/app/services/filter.service';
import {
  FloatingChatNavigationAction,
  FloatingChatStateService,
} from 'src/app/services/floating-chat-state.service';

@Component({
  selector: 'app-floating-chatbot',
  templateUrl: './floating-chatbot.component.html',
  styleUrls: ['./floating-chatbot.component.scss']
})
export class FloatingChatbotComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly EXPAND_ANIMATION_MS = 300;
  private static readonly CHAT_RESET_OUT_MS = 240;
  private static readonly CHAT_RESET_IN_MS = 320;

  isOpen = false;
  isVisible = false;
  isChatExpanded = false;
  activeBrandKey: number | null = null;
  pageName = '';
  chatTransitionState: 'idle' | 'out' | 'in' = 'idle';

  @ViewChild(ScraiiAiComponent) scraiiAi?: ScraiiAiComponent;
  @ViewChild('chatbotPopup') chatbotPopup?: ElementRef<HTMLElement>;

  private routerSubscription?: Subscription;
  private filterQuerySubscription?: Subscription;
  private pageContextSubscription?: Subscription;
  private lastDashboardKey: number | null = null;
  private lastPageContextKey: string | null = null;
  private currentUrl = '';
  private pendingSnapshotRestore = false;
  private popupResizeObserver?: ResizeObserver;
  private relayoutTimer?: ReturnType<typeof setTimeout>;
  private flipFallbackTimer?: ReturnType<typeof setTimeout>;
  private isAnimatingExpand = false;
  private flipTransitionEndHandler?: (event: TransitionEvent) => void;
  private chatTransitionOutTimer?: ReturnType<typeof setTimeout>;
  private chatTransitionInTimer?: ReturnType<typeof setTimeout>;
  private pendingAnimatedReset?: () => void;

  private readonly onPopupTransitionEnd = (event: TransitionEvent): void => {
    if (event.target !== this.chatbotPopup?.nativeElement) {
      return;
    }
    if (event.propertyName === 'width' || event.propertyName === 'height' || event.propertyName === 'transform') {
      this.relayoutEmbeddedCharts();
    }
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private filterService: FilterService,
    private floatingChatState: FloatingChatStateService
  ) {}

  ngOnInit() {
    this.currentUrl = this.router.url;
    this.updateVisibility(this.currentUrl);
    this.syncFloatingContext();

    this.filterQuerySubscription = this.filterService.filterQuery.subscribe(() => {
      this.updateVisibility(this.currentUrl);
      this.syncFloatingContext();
    });

    this.pageContextSubscription = this.floatingChatState.onPageContextPublished.subscribe(() => {
      this.syncFloatingContext();
    });

    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const prevUrl = this.currentUrl;
      this.currentUrl = event.urlAfterRedirects;

      const enteringAiRoute =
        !this.floatingChatState.isScraiiAiRoute(prevUrl) &&
        this.floatingChatState.isScraiiAiRoute(this.currentUrl);

      if (enteringAiRoute) {
        this.floatingChatState.clearAll();
        this.scraiiAi?.startNewChat();
      }

      const wasVisible = this.isVisible;
      this.updateVisibility(event.urlAfterRedirects);

      if (!wasVisible && this.isVisible) {
        this.pendingSnapshotRestore = true;
      }

      this.syncFloatingContext();
    });
  }

  ngAfterViewInit() {
    if (this.isVisible) {
      this.setupPopupResizeObserver();
    }
    this.chatbotPopup?.nativeElement?.addEventListener('transitionend', this.onPopupTransitionEnd);
    this.applyPendingSnapshotRestore();
  }

  ngOnDestroy() {
    this.cleanupFlipAnimation();
    this.clearChatTransitionTimers();
    this.chatbotPopup?.nativeElement?.removeEventListener('transitionend', this.onPopupTransitionEnd);
    this.routerSubscription?.unsubscribe();
    this.filterQuerySubscription?.unsubscribe();
    this.pageContextSubscription?.unsubscribe();
    this.popupResizeObserver?.disconnect();
    if (this.relayoutTimer) {
      clearTimeout(this.relayoutTimer);
    }
    this.setBodyScrollLock(false);
  }

  updateVisibility(url: string) {
    const shouldShow =
      this.authService.isLoggedIn() &&
      !this.isAuthRoute(url) &&
      !this.floatingChatState.isScraiiAiRoute(url) &&
      this.floatingChatState.shouldShowFloatingChat(url);

    if (!shouldShow) {
      this.cleanupFlipAnimation();
      this.isOpen = false;
      this.isChatExpanded = false;
      this.setBodyScrollLock(false);
    }

    this.isVisible = shouldShow;
    if (shouldShow) {
      setTimeout(() => {
        this.setupPopupResizeObserver();
        this.applyPendingSnapshotRestore();
      }, 0);
    } else {
      this.popupResizeObserver?.disconnect();
      this.popupResizeObserver = undefined;
    }
  }

  private isAuthRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || path === '' || path.startsWith('/auth');
  }

  private exportLiveSnapshot() {
    return this.scraiiAi?.exportFloatingChatSnapshot() ?? null;
  }

  private saveCurrentPageSnapshot(): void {
    const pageContextKey = this.floatingChatState.resolvePageContextKey(this.currentUrl);
    const snapshot = this.exportLiveSnapshot();
    if (snapshot) {
      this.floatingChatState.saveSnapshot(pageContextKey, snapshot);
    }
  }

  private restorePageSnapshotIfNeeded(): void {
    if (!this.scraiiAi) {
      this.pendingSnapshotRestore = true;
      return;
    }

    const pageContextKey = this.floatingChatState.resolvePageContextKey(this.currentUrl);
    if (!this.floatingChatState.isPersistableContextKey(pageContextKey)) {
      return;
    }

    const snapshot = this.floatingChatState.getSnapshot(pageContextKey);
    if (snapshot) {
      this.scraiiAi.restoreFloatingChatSnapshot(snapshot);
    }
    this.pendingSnapshotRestore = false;
  }

  private applyPendingSnapshotRestore(): void {
    if (!this.pendingSnapshotRestore || !this.isVisible) {
      return;
    }
    this.restorePageSnapshotIfNeeded();
  }

  private applyNavigationAction(action: FloatingChatNavigationAction): void {
    switch (action.type) {
      case 'restore':
        this.runAnimatedChatRestore(() => {
          this.scraiiAi?.restoreFloatingChatSnapshot(action.snapshot);
        });
        break;
      case 'resetLive':
      case 'clearAllAndReset':
        this.runAnimatedChatReset(() => this.scraiiAi?.startNewChat());
        break;
      case 'none':
        break;
    }
  }

  private syncFloatingContext(): void {
    const onDashboard = this.floatingChatState.isDashboardRoute(this.currentUrl);
    const newPageContextKey = this.floatingChatState.resolvePageContextKey(this.currentUrl);
    const liveSnapshot = this.exportLiveSnapshot();

    if (!onDashboard) {
      if (
        this.lastPageContextKey &&
        this.floatingChatState.isPersistableContextKey(this.lastPageContextKey) &&
        liveSnapshot &&
        !this.floatingChatState.isScraiiAiRoute(this.currentUrl)
      ) {
        this.floatingChatState.saveSnapshot(this.lastPageContextKey, liveSnapshot);
      }
      this.activeBrandKey = null;
      this.pageName = '';
      this.floatingChatState.clearActiveDashboardPages();
      this.lastPageContextKey = 'off-dashboard';
      return;
    }

    const dashboardKey = this.floatingChatState.isInsideWorkRoute(this.currentUrl)
      ? this.floatingChatState.resolveDashboardKey(this.currentUrl)
      : null;
    const dashboardAction = this.floatingChatState.handleDashboardSwitch(
      this.lastDashboardKey,
      dashboardKey,
      this.lastPageContextKey,
      newPageContextKey
    );
    this.applyNavigationAction(dashboardAction);

    if (dashboardKey != null) {
      this.lastDashboardKey = dashboardKey;
    }

    if (newPageContextKey !== this.lastPageContextKey) {
      const pageAction = this.floatingChatState.handlePageContextTransition(
        this.lastPageContextKey,
        newPageContextKey,
        liveSnapshot
      );
      this.applyNavigationAction(pageAction);
      this.lastPageContextKey = newPageContextKey;
    }

    const brandKey = this.floatingChatState.resolveDashboardBrandKey(this.currentUrl);
    if (brandKey !== this.activeBrandKey) {
      this.activeBrandKey = brandKey;
    }

    this.pageName = this.floatingChatState.resolvePageName(this.currentUrl);
  }

  toggleChat() {
    if (this.isOpen) {
      this.saveCurrentPageSnapshot();
    }

    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.cleanupFlipAnimation();
      this.isChatExpanded = false;
      this.setBodyScrollLock(false);
    } else {
      setTimeout(() => {
        if (this.pendingSnapshotRestore) {
          this.restorePageSnapshotIfNeeded();
        }
        this.syncFloatingContext();
        this.scraiiAi?.syncActiveTextareaHeight();
      }, 0);
      this.relayoutEmbeddedCharts();
    }
  }

  callNewChat(): void {
    this.runAnimatedChatReset(() => {
      this.scraiiAi?.startNewChat();
      this.saveCurrentPageSnapshot();
    });
  }

  expandChat(): void {
    if (!this.isOpen || this.isAnimatingExpand) {
      return;
    }

    const popup = this.chatbotPopup?.nativeElement;
    if (!popup) {
      this.toggleExpandedState();
      this.finalizeExpandAnimation();
      return;
    }

    if (this.prefersReducedMotion()) {
      this.toggleExpandedState();
      this.finalizeExpandAnimation();
      return;
    }

    this.animateExpandCollapse(popup);
  }

  private toggleExpandedState(): void {
    this.isChatExpanded = !this.isChatExpanded;
    this.setBodyScrollLock(this.isOpen && this.isChatExpanded);
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private animateExpandCollapse(popup: HTMLElement): void {
    this.isAnimatingExpand = true;

    const first = popup.getBoundingClientRect();
    this.toggleExpandedState();

    popup.classList.add('is-flip-animating');
    void popup.offsetHeight;

    const last = popup.getBoundingClientRect();
    const deltaX = first.left - last.left;
    const deltaY = first.top - last.top;
    const scaleX = last.width > 0 ? first.width / last.width : 1;
    const scaleY = last.height > 0 ? first.height / last.height : 1;

    popup.style.transition = 'none';
    popup.style.transformOrigin = 'top left';
    popup.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

    this.flipTransitionEndHandler = (event: TransitionEvent) => {
      if (event.target !== popup || event.propertyName !== 'transform') {
        return;
      }
      this.finishFlipAnimation(popup);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        popup.style.transition = '';
        popup.style.transform = '';
        popup.addEventListener('transitionend', this.flipTransitionEndHandler!);

        if (this.flipFallbackTimer) {
          clearTimeout(this.flipFallbackTimer);
        }
        this.flipFallbackTimer = setTimeout(() => {
          if (this.isAnimatingExpand) {
            this.finishFlipAnimation(popup);
          }
        }, FloatingChatbotComponent.EXPAND_ANIMATION_MS + 50);
      });
    });
  }

  private finishFlipAnimation(popup: HTMLElement): void {
    if (!this.isAnimatingExpand) {
      return;
    }

    if (this.flipTransitionEndHandler) {
      popup.removeEventListener('transitionend', this.flipTransitionEndHandler);
      this.flipTransitionEndHandler = undefined;
    }

    if (this.flipFallbackTimer) {
      clearTimeout(this.flipFallbackTimer);
      this.flipFallbackTimer = undefined;
    }

    popup.classList.remove('is-flip-animating');
    popup.style.transition = '';
    popup.style.transform = '';
    popup.style.transformOrigin = '';
    this.isAnimatingExpand = false;
    this.finalizeExpandAnimation();
  }

  private cleanupFlipAnimation(): void {
    const popup = this.chatbotPopup?.nativeElement;
    if (!popup) {
      this.isAnimatingExpand = false;
      return;
    }

    if (this.flipTransitionEndHandler) {
      popup.removeEventListener('transitionend', this.flipTransitionEndHandler);
      this.flipTransitionEndHandler = undefined;
    }

    if (this.flipFallbackTimer) {
      clearTimeout(this.flipFallbackTimer);
      this.flipFallbackTimer = undefined;
    }

    popup.classList.remove('is-flip-animating');
    popup.style.transition = '';
    popup.style.transform = '';
    popup.style.transformOrigin = '';
    this.isAnimatingExpand = false;
  }

  private finalizeExpandAnimation(): void {
    this.relayoutEmbeddedCharts();
    setTimeout(() => this.scraiiAi?.syncActiveTextareaHeight(), 0);
  }

  private setupPopupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined' || !this.chatbotPopup?.nativeElement) {
      return;
    }

    this.popupResizeObserver?.disconnect();
    this.popupResizeObserver = new ResizeObserver(() => {
      if (this.isOpen) {
        this.relayoutEmbeddedCharts();
      }
    });
    this.popupResizeObserver.observe(this.chatbotPopup.nativeElement);
  }

  private relayoutEmbeddedCharts(): void {
    if (this.relayoutTimer) {
      clearTimeout(this.relayoutTimer);
    }
    this.relayoutTimer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.scraiiAi?.relayoutFloatingContent();
        });
      });
    }, FloatingChatbotComponent.EXPAND_ANIMATION_MS);
  }

  private setBodyScrollLock(locked: boolean): void {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  private shouldAnimateChatTransition(): boolean {
    return this.isOpen && !this.prefersReducedMotion();
  }

  private runAnimatedChatReset(resetFn: () => void): void {
    if (!this.shouldAnimateChatTransition()) {
      resetFn();
      return;
    }

    this.pendingAnimatedReset = resetFn;
    if (this.chatTransitionState !== 'idle') {
      return;
    }

    this.chatTransitionState = 'out';
    this.chatTransitionOutTimer = setTimeout(() => {
      this.chatTransitionOutTimer = undefined;
      const action = this.pendingAnimatedReset;
      this.pendingAnimatedReset = undefined;
      action?.();

      this.chatTransitionState = 'in';
      this.chatTransitionInTimer = setTimeout(() => {
        this.chatTransitionInTimer = undefined;
        this.chatTransitionState = 'idle';
      }, FloatingChatbotComponent.CHAT_RESET_IN_MS);
    }, FloatingChatbotComponent.CHAT_RESET_OUT_MS);
  }

  private runAnimatedChatRestore(restoreFn: () => void): void {
    if (!this.shouldAnimateChatTransition()) {
      restoreFn();
      return;
    }

    restoreFn();
    this.chatTransitionState = 'in';
    this.clearChatTransitionTimers();
    this.chatTransitionInTimer = setTimeout(() => {
      this.chatTransitionInTimer = undefined;
      this.chatTransitionState = 'idle';
    }, FloatingChatbotComponent.CHAT_RESET_IN_MS);
  }

  private clearChatTransitionTimers(): void {
    if (this.chatTransitionOutTimer) {
      clearTimeout(this.chatTransitionOutTimer);
      this.chatTransitionOutTimer = undefined;
    }
    if (this.chatTransitionInTimer) {
      clearTimeout(this.chatTransitionInTimer);
      this.chatTransitionInTimer = undefined;
    }
    this.pendingAnimatedReset = undefined;
    this.chatTransitionState = 'idle';
  }
}
